import type { GetServerSidePropsContext } from 'next'

import { paths } from '@mc/paths'
import { PROMO_FEATURE_FLAG, shouldShowPromotion } from '@mc/promo-utils'
import { getBucket, getExperimentVariables } from '@mc/next-growthbook'

import { HeroImage } from 'components/HomepageCarousel/Carousel/constants'
import {
  getAllGrowthClasses,
  getGrowthCategoriesAndSubcategories,
} from 'libs/server/api/growth'
import { getIsSeries } from 'libs/utils'

import {
  CarouselItem,
  CarouselItemType,
  type CarouselImage,
  homepageCarouselImages,
} from './constants'

export const convertCarouselImagesToHeroImagePairs = (): HeroImage[] =>
  homepageCarouselImages.reduce<HeroImage[]>((acc, _, index, array) => {
    if (index % 2 === 0) {
      acc.push({
        firstColumn: array[index].carouselImageURL,
        secondColumn: array[index + 1]?.carouselImageURL,
      })
    }
    return acc
  }, [])

export const getCarouselItems = async (
  ctx: GetServerSidePropsContext,
): Promise<CarouselItem[]> => {
  const { categories, subcategories } =
    await getGrowthCategoriesAndSubcategories(ctx)

  const courses = await getAllGrowthClasses(ctx)

  const categoriesMap = new Map()

  categories.forEach((category) => {
    categoriesMap.set(category.id, category.name)
  })

  const buildPrimaryCategory = (categoryId?: number) => {
    if (!categoryId) return undefined

    return {
      id: categoryId,
      name: categoriesMap.get(categoryId),
    }
  }

  // if promotion is on, we need to get the homepageCarouselImages from the FV
  const promoBucket = await getBucket(ctx, PROMO_FEATURE_FLAG)
  const { isPromotionOn } = shouldShowPromotion({ promoBucket })
  const variables = await getExperimentVariables(ctx, PROMO_FEATURE_FLAG)
  const promoImages =
    (variables?.home_page_hero_images_pen_4889_v1 as CarouselImage[]) ||
    homepageCarouselImages
  const images = isPromotionOn ? promoImages : homepageCarouselImages

  return images.map((carouselImage) => {
    if (carouselImage.itemType === 'subcategory') {
      const subcategory = subcategories?.find(
        (subcat) => subcat.slug === carouselImage.slug,
      )

      return {
        ...carouselImage,
        link: paths.subcategoryPage(subcategory?.slug),
        title: subcategory?.name || carouselImage.slug,
        nameplateText: subcategory?.name,
        primaryCategory: buildPrimaryCategory(subcategory?.parentId),
      }
    }

    const course = courses?.find((c) => c.slug === carouselImage.slug)

    return {
      ...carouselImage,
      itemType: course?.type as CarouselItemType,
      link: paths.courseTypePage(course?.type || 'course', carouselImage.slug),
      title: getIsSeries(course?.type)
        ? course?.title || carouselImage.slug
        : course?.instructorName ?? carouselImage.slug,
      nameplateImage: course?.nameplate,
      primaryCategory: buildPrimaryCategory(course?.primaryCategory.id),
    }
  })
}
